'use client';

import React from 'react';

import type { Stage } from 'corgi-types';
import CommonDrawer from 'corgi/common-drawer/components/CommonDrawer';
import SmartBanner from 'corgi/common-smart-banner-dy/components/CommonSmartBanner';
import ErrorBoundary from 'corgi/common/components/ErrorBoundary';
import RouterProvider from 'corgi/common/providers/RouterProvider';
import GlobalHead from 'corgi/global-head/components/GlobalHead';
import type { GlobalNavigationProps } from 'frontend-utils/global-nav';

import { LogoutBanner } from '@components/common/fragments/AutoLogoutBanner';
import Footer from '@components/common/fragments/Footer';
import Header from '@components/common/fragments/Header';
import { GTMScripts } from '@components/common/scripts/GTM';
import type { ContentPageProps } from '@local-types/content/props';
import { arePropsNonEmpty } from '@utils/render';
import { useAppRouter } from '@utils/router/use-router';
import ErrorPage from '../../app/error';
import { ContentBody } from './ContentBody';

interface Props {
  contentPageProps: Omit<ContentPageProps, 'wishlistState'>;
  globalNavProps: GlobalNavigationProps;
  stage: Stage;
  shouldInjectGtmScript: boolean;
  children?: React.ReactNode;
}

export default function ContentPage({
  contentPageProps,
  globalNavProps,
  stage,
  shouldInjectGtmScript,
  children,
}: Props) {
  const {
    dataLayer,
    globalHeadProps,
    smartBannerProps,
    firstTopTeaserImageProps,
    ...componentsProps
  } = contentPageProps;

  const router = useAppRouter();

  return (
    <ErrorBoundary FallbackComponent={ErrorPage}>
      <RouterProvider router={router}>
        {/* head */}
        <GlobalHead {...globalHeadProps} />

        {/* scripts */}
        <GTMScripts
          id="content"
          dataLayer={dataLayer}
          stage={stage}
          withScripts={shouldInjectGtmScript}
        />

        {/* Top Section */}
        <LogoutBanner />
        {arePropsNonEmpty(smartBannerProps) && <SmartBanner {...smartBannerProps} />}

        <Header {...globalNavProps} />

        <CommonDrawer />

        <main>
          <ContentBody {...componentsProps} />
          {children}
        </main>

        <Footer {...globalNavProps} />
      </RouterProvider>
    </ErrorBoundary>
  );
}
